import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button } from "@mui/material";
import checkAll from "@iconify/icons-mdi/check-all";
import { InlineIcon } from "@iconify/react";
import { MyHrContractCard } from "my-hr/components";
import {
  MyTimesheetMyTimesheetTable,
  MyTimesheetPageTitle,
} from "my-timesheet/components";
import { MY_TIMESHEET_APPROVE_TIMESHEETS_BY_CONTRACT } from "my-timesheet/graphql";
import {
  useMySheetCurrentRoleState,
  useMyTimesheetDashboardDate,
  useMyTimesheetRequestsByContract,
} from "my-timesheet/hooks";
import { OcCard, OcPageTransition } from "oc/components";
import { useOcCurrentUserState, useOcSnackbar } from "oc/hooks";
import useOcButtonGroup from "oc/hooks/use-button-group";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import useStyles from "my-leave/components/filter-bar/filter-bar.style";

export const MyTimesheetRequestsByContractPage = () => {
  const { contract } = useParams<{ contract: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const { setOpenMessage } = useOcSnackbar();
  const isMobile = useOcIsMobile();
  let { contractCode } = useOcCurrentUserState();
  const classes = useStyles({ isMobile, createLeaveRequestIsEnabled: "false" });

  const { state: currentRole } = useMySheetCurrentRoleState();
  const isExaminer = currentRole === "examiner";

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: `MySheetRequestsByContract-${contract}`,
    buttonsDef,
  });

  const showOnlyRequests = selectedButton === "OC_TO_BE_ASSESSED";

  const {
    currentDate,
    period,
    handlePeriodChange,
    handleChangeCurrentDate,
    periodStart,
    periodEnd,
  } = useMyTimesheetDashboardDate(`examinerContract${contract}`);

  const { contractData, employeeData, data, loading, error } =
    useMyTimesheetRequestsByContract({
      contract,
      periodStart,
      periodEnd,
      showOnlyRequests,
    });

  const handleShowRequest = (request: string) => {
    const url = `/my-timesheet/user-request/${request}`;
    history.push(url);
  };

  const [approveAllRequests] = useMutation(
    MY_TIMESHEET_APPROVE_TIMESHEETS_BY_CONTRACT,
    {
      onCompleted: () => {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_ALL_APPROVE_SUCEEDED"),
        });
        history.push("/my-timesheet/user-requests");
      },
      onError: (error) => {
        setOpenMessage({
          type: "error",
          message: t("MY_TIME_APPROVE_FAILED"),
        });
        console.log({ error });
      },
    },
  );

  const handleApproveAllRequests = () => {
    approveAllRequests({
      variables: {
        contract: contract,
      },
    });
  };

  const disabled = contract === contractCode;

  const hasRequestedItems =
    Array.isArray(data) &&
    data.some((week) =>
      Array.isArray(week?.timesheets) &&
      week.timesheets.some((day) =>
        Array.isArray(day?.timesheets) &&
        day.timesheets.some((ts) => ts.state === "requested")
      )
    );

  return (
    <OcPageTransition>
      <Box padding={isMobile ? "0px" : "16px"}>
        <MyTimesheetPageTitle
          title={isExaminer ? t("OC_APPROVE_REQUESTS") : t("OC_REQUESTS_VIEW")}
          showDateRange={true}
          currentDate={currentDate}
          handleChangeCurrentDate={handleChangeCurrentDate}
          period={period === "week" || period === "month" ? period : undefined}
          handlePeriodChange={handlePeriodChange}
          goBack={true}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <MyHrContractCard employee={employeeData} contract={contractData} />
        <Box height="32px" />
        <OcCard
          label={t("MY_TIMESHEET_REQUESTS")}
          loading={loading}
          error={!!error}
        >
          <Box padding="32px 0px 0px 0px">
            <Box
              display="flex"
              flexDirection={isMobile ? "column-reverse" : "row"}
            >
              {isExaminer && <Box width="200px" />}
              <Box
                flexGrow={1}
                display="flex"
                flexDirection="row"
                justifyContent="center"
              >
                <OcButtonGroup />
              </Box>
              {isMobile && <Box height="36px" />}
              {isExaminer && (
                <Box width={isMobile ? "unset" : "200px"} textAlign="right">
                  <Button
                    id="all-approve"
                    variant="outlined"
                    color="secondary"
                    startIcon={<InlineIcon icon={checkAll} />}
                    onClick={handleApproveAllRequests}
                    disableElevation
                    className={classes.allApprove}
                    disabled={loading || disabled || !hasRequestedItems}
                    sx={{
                      backgroundColor: "#E3E5E5",
                      color: "#212121",
                    }}
                  >
                    {t("OC_ALL_APPROVE")}
                  </Button>
                </Box>
              )}
            </Box>
            <Box height="32px" />
            <MyTimesheetMyTimesheetTable
              showDateSelector={false}
              data={data}
              currentDate={currentDate}
              onChangeCurrentDate={() => { }}
              onShowRequest={handleShowRequest}
              period={period}
              onChangePeriod={() => { }}
              showOnlyRequests={showOnlyRequests}
              loading={loading}
              error={error}
            />
          </Box>
        </OcCard>
      </Box>
    </OcPageTransition>
  );
};

export default MyTimesheetRequestsByContractPage;
